import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import FooterDrawer from '@/components/ui/atoms/FooterDrawer';
import MInputField from '@/components/ui/atoms/MInputField';
import MDropDownDrawer from "@/components/ui/molecules/MDropDownDrawer";
import MTextArea from '@/components/ui/atoms/MTextArea';
import { MButton } from '@/components/ui/atoms/MButtons';
import { FileCategory } from '@/types/document.dto';
import { useMembers, getMemberInfo } from '@/hooks/useMembers';
import { FileCategoryLabel } from '@/config/document-table.config';
import styles from './document.module.css';
import { IconCaretDownFilled } from "@tabler/icons-react";
import MemberPickerDrawer from '@/components/ui/atoms/MemberPickerDrawer';
import { trackUserDataLoaded } from '@/utils/gtm';
import { ANALYTICS_SCREEN_TYPES, ANALYTICS_SCREEN_NAMES } from '@/constants/analytics.constants';

interface UncategorizedDrawerProps {
  open: boolean;
  onClose: () => void;
  document: any;
  onDone: (fields: {
    displayName: string;
    assignedTo: any;
    fileCategory: FileCategory;
    comment: string;
  }) => void;
}

const UncategorizedDrawer: React.FC<UncategorizedDrawerProps> = ({ open, onClose, document, onDone }) => {
  const navigate = useNavigate();
  const currentMemberInfo = getMemberInfo();
  // Local managed members and selected member state
  const [members, setMembers] = useState<any[]>([]);
  const [assignedTo, setAssignedTo] = useState<any>(document?.assignedTo || null);
  const { getManagedMembers } = useMembers({ autoFetch: false });

  useEffect(() => {
    if (open) {
      getManagedMembers().then(setMembers);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const [displayName, setDisplayName] = useState(document?.displayName || document?.media?.fileName || '');
  const [showMemberDrawer, setShowMemberDrawer] = useState(false);
  const [fileCategory, setFileCategory] = useState<FileCategory | null>(document?.fileCategory || document?.category || null);
  const [showCategoryDrawer, setShowCategoryDrawer] = useState(false);
  const [comment, setComment] = useState(document?.notes || '');
  const [submitting, setSubmitting] = useState(false);

  // Reset fields when document or open changes
  useEffect(() => {
    setDisplayName(document?.displayName || document?.media?.fileName || '');
    setFileCategory(document?.fileCategory || document?.category || null);
    setComment(document?.notes || '');
    setAssignedTo(document?.assignedTo || null);
    
    // Track when drawer opens (edit click)
    if (open) {
      trackUserDataLoaded({
        memberInfo: currentMemberInfo,
        screenType: ANALYTICS_SCREEN_TYPES.DOCUMENTS,
        screenName: ANALYTICS_SCREEN_NAMES.DOCUMENTS_ADD_PROCESSING_EDIT_FILE,
      });
    }
  }, [document, open]);

  // Validation
  const isValid = assignedTo && fileCategory !== null;

  return (
    <FooterDrawer open={open} onClose={onClose} title={"Edit File"}>
      <div className={styles.uncategorizedDrawerContent}>
        {/* Document Name */}
        <MInputField
          label="Document name"
          value={displayName}
          placeholder='Not Answered'
          onChange={e => setDisplayName(e.target.value)}
          isDrawerInput
          
        />
        {/* Assigned To */}
        <MInputField
          label="Document owner name"
          value={assignedTo ? `${assignedTo.first_name || ''}${assignedTo.last_name ? ' ' + assignedTo.last_name : ''}` : ''}
          onClick={() => setShowMemberDrawer(true)}
          placeholder="Select owner"
          readOnly
          mandatory
          showIcon
          icon={<IconCaretDownFilled />}
          isDrawerInput
        />
        <MemberPickerDrawer
          open={showMemberDrawer}
          members={members}
          selectedId={assignedTo?.id}
          onSelect={member => {
            setAssignedTo(member);
            setShowMemberDrawer(false);
          }}
          onClose={() => setShowMemberDrawer(false)}
          title="Select Owner"
        />
        {/* Type of Document */}
        <MInputField
          label="Document type"
          value={fileCategory !== null && FileCategoryLabel[fileCategory] ? FileCategoryLabel[fileCategory] : ''}
          onClick={() => setShowCategoryDrawer(true)}
          placeholder="Select type"
          readOnly
          mandatory
          showIcon
          icon={<IconCaretDownFilled />}
          isDrawerInput
        />
        <MDropDownDrawer
          open={showCategoryDrawer}
          title="Select document type"
          items={Object.values(FileCategory)
            .filter(v => typeof v === 'number' && v !== FileCategory.Other)
            .map(v => ({
              value: String(v),
              label: FileCategoryLabel[v as FileCategory],
            }))}
          initialValue={fileCategory !== null ? String(fileCategory) : ''}
          onSelect={value => {
            setFileCategory(Number(value) as FileCategory);
            setShowCategoryDrawer(false);
          }}
          onClose={() => setShowCategoryDrawer(false)}
        />
        {/* Comment */}
        <MTextArea
          label="Add comment (optional)"
          value={comment}
          onChange={e => setComment(e.target.value)}
          rows={4}
          maxLength={250}
          variant='drawerInput'
        />
        <MButton
          variant="filled"
          size="lg"
          style={{ marginTop: 16, width: '100%' }}
          disabled={!isValid || submitting || !displayName || displayName.trim() === ''}
          onClick={async () => {
            setSubmitting(true);
            
            // Track done click
            trackUserDataLoaded({
              memberInfo: currentMemberInfo,
              screenType: ANALYTICS_SCREEN_TYPES.DOCUMENTS,
              screenName: ANALYTICS_SCREEN_NAMES.DOCUMENTS_ADD_PROCESSING_EDIT_FILE_DONE,
            });
            
            await onDone({ displayName, assignedTo, fileCategory: fileCategory!, comment });
            setSubmitting(false);
          }}
        >
          Done
        </MButton>
      </div>
    </FooterDrawer>
  );
};

export default UncategorizedDrawer;
